import React from 'react';
import { prop, uniq, drop, dropLast } from 'ramda';
import { connect } from 'react-redux';
import Media from 'react-media';
import styled, { withTheme } from 'styled-components';
import { objectOf, number } from 'prop-types';
import ContainerDimensions from 'react-container-dimensions';
import { scaleLinear } from 'd3-scale';
import Paper from 'material-ui/Paper';
import { TinyNote } from '../Text';
import { getSingleValueData } from '../../selectors/crimes';
import { valueFormat } from '../../utils/format';
import { getExtent } from '../../utils/helpers';

const Container = styled(Paper)`
  position: relative;
  width: ${prop('width')}px;
  padding: ${prop('padding')}px;
  overflow: hidden;
`;

const Labels = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 14px;
`;

const Label = TinyNote.extend`
  position: absolute;
  padding: 0 4px;
  line-height: 1;
  transform: translate(${prop('x')}px);
  background: white;

  &:last-child {
    transform: translate(${({ x }) => `calc(${x}px - 100%)`});
  }
`;

const Gradient = styled.div`
  width: 100%;
  height: ${prop('height')}px;
  background: linear-gradient(to right, ${({ values }) => values.join(', ')});
`;

const Tick = styled.div`
  position: absolute;
  top: ${prop('padding')}px;
  bottom: ${prop('padding')}px;
  transform: translate(${prop('x')}px);
  width: 1px;
  background: ${({ theme }) => theme.colors.grey300};
`;

const getScale = (width, extent, padding) =>
  scaleLinear()
    .domain(extent)
    .range([0, width - 2 * padding]);

const getTicks = (scale, min, max) => {
  const unadjustedTicks = scale.ticks(5);
  const withoutFirst = drop(1, unadjustedTicks);
  const withoutFirstOrLast = dropLast(1, withoutFirst);
  return uniq([min, ...withoutFirstOrLast, max]);
};

export const MapLegend = withTheme(({ theme, data }) => {
  const [min, max] = getExtent(data);
  const gradient =
    min !== max
      ? min < 0 ? theme.gradients.compare : theme.gradients.absolute
      : theme.gradients.blank;

  return (
    <Media query={theme.media.tablet}>
      {tablet => {
        const padding = tablet ? 2 : 4;
        return (
          <Container
            padding={padding}
            width={480}
            style={{
              display: tablet ? 'none' : 'block',
            }}
          >
            <ContainerDimensions>
              {({ width }) => {
                const scale = getScale(width, [min, max], padding);
                const ticks = getTicks(scale, min, max);

                return (
                  <React.Fragment>
                    {!tablet ? (
                      <Labels>
                        {ticks.map(value => (
                          <Label key={`tick-${value}`} x={scale(value)}>
                            {valueFormat(value)}
                          </Label>
                        ))}
                      </Labels>
                    ) : null}
                    {!tablet ? <Gradient values={gradient} height={8} /> : null}
                    {!tablet
                      ? ticks.map(value => (
                          <Tick
                            key={value}
                            padding={padding}
                            x={scale(value)}
                          />
                        ))
                      : null}
                  </React.Fragment>
                );
              }}
            </ContainerDimensions>
          </Container>
        );
      }}
    </Media>
  );
});

MapLegend.propTypes = {
  data: objectOf(number).isRequired,
};

export default connect(state => ({
  data: getSingleValueData(state),
}))(MapLegend);



// WEBPACK FOOTER //
// ./src/components/MapPage/MapLegend.jsx